/**
 * Turning a Looker tile into what a chart draws. Pure, so each rule — which
 * axis, which series stacks, where a waterfall bar floats — is tested here
 * rather than through an SVG jsdom cannot lay out.
 *
 * null stays null throughout: a missing value is "no data", never 0.
 *
 * @module components/looker/lookerChartData
 */
import type {
  LookerCell, LookerComparison, LookerConditionalFormat, LookerField, LookerReferenceValue, LookerSeriesType, LookerTile,
} from '@/services/looker/lookerTypes';

/** Google palette — see the palette memory: Google grey, not Tailwind. */
export const SERIES_COLORS = ['#1a73e8', '#34a853', '#fbbc04', '#ea4335', '#5f6368', '#9334e6', '#12b5cb', '#e8710a'];

export const toChartNumber = (v: LookerCell['value']): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const cellText = (c: LookerCell | undefined): string | null =>
  c?.rendered ?? (c?.value == null ? null : String(c.value));

/**
 * The width a Looker picture is asked for: the card's, snapped to 40 px so
 * near-identical cards share the server's cached picture, within what the
 * route accepts (240–1600).
 */
export const renderWidthFor = (cardWidth: number): number =>
  Math.min(1600, Math.max(240, Math.round((Number.isFinite(cardWidth) ? cardWidth : 0) / 40) * 40));

/** Axis ticks and labels: "8.5K", "1.4M"; a small ratio keeps its digits. */
export const compactNumber = (v: number): string => {
  const abs = Math.abs(v);
  const [div, suffix] = abs >= 1e9 ? [1e9, 'B'] : abs >= 1e6 ? [1e6, 'M'] : abs >= 1e3 ? [1e3, 'K'] : [1, ''];
  if (!suffix) return v.toLocaleString('en-US', { maximumFractionDigits: abs < 1 ? 3 : 2 });
  const n = v / div;
  return `${n.toLocaleString('en-US', { maximumFractionDigits: Math.abs(n) < 10 ? 1 : 0 })}${suffix}`;
};

/** A Looker day dimension ("2026-10-02"), which the axis shortens with shortDate. */
export const isIsoDate = (x: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(x);

export type ChartPoint = Record<string, string | number | null> & { x: string };

/** One point per row: x is the first dimension's text, each measure a number or null. */
export const chartRows = (tile: LookerTile): { dim: LookerField | undefined; measures: LookerField[]; data: ChartPoint[] } => {
  const dim = tile.fields.find((f) => f.kind === 'dimension');
  const measures = tile.fields.filter((f) => f.kind === 'measure');
  const data = tile.rows.map((r) => ({
    x: dim ? cellText(r[dim.name]) ?? '—' : '',
    ...Object.fromEntries(measures.map((m) => [m.name, toChartNumber(r[m.name]?.value ?? null)])),
  })) as ChartPoint[];
  return { dim, measures, data };
};

export interface CartesianSeries {
  name: string;
  label: string;
  type: LookerSeriesType;
  axis: 'left' | 'right';
  /** Same id = drawn on top of each other. Only bars and areas stack, one stack per axis. */
  stackId: string | undefined;
  color: string;
}

const BASE_TYPE: Partial<Record<LookerTile['type'], LookerSeriesType>> = {
  line: 'line',
  area: 'area',
  bar: 'bar',
  scatter: 'scatter',
};

export const cartesianSeries = (tile: LookerTile): CartesianSeries[] => {
  const base = BASE_TYPE[tile.type] ?? 'bar';
  const right = new Set(tile.vis.rightAxis);
  return tile.fields
    .filter((f) => f.kind === 'measure')
    .map((m, i) => {
      const type = tile.vis.seriesTypes[m.name] ?? base;
      const axis = right.has(m.name) ? 'right' : 'left';
      return {
        name: m.name,
        label: m.label,
        type,
        axis,
        stackId: tile.vis.stacking && (type === 'bar' || type === 'area') ? axis : undefined,
        color: tile.vis.seriesColors[m.name] ?? SERIES_COLORS[i % SERIES_COLORS.length],
      };
    });
};

export interface WaterfallStep {
  x: string;
  /** Invisible bar under the visible one. */
  base: number;
  size: number;
  value: number;
  kind: 'up' | 'down' | 'total';
}

/** Each step floats from the running total; a closing bar shows the total. Missing values are skipped, not zeroed. */
export const waterfallSteps = (tile: LookerTile, totalLabel: string): WaterfallStep[] => {
  const { measures, data } = chartRows(tile);
  const m = measures[0];
  if (!m) return [];
  let running = 0;
  const steps: WaterfallStep[] = [];
  for (const p of data) {
    const v = p[m.name];
    if (typeof v !== 'number') continue;
    const next = running + v;
    steps.push({ x: p.x, base: Math.min(running, next), size: Math.abs(v), value: v, kind: v >= 0 ? 'up' : 'down' });
    running = next;
  }
  return steps.length === 0
    ? []
    : [...steps, { x: totalLabel, base: Math.min(0, running), size: Math.abs(running), value: running, kind: 'total' }];
};

/**
 * Funnel stages: the rows when there is a dimension (one stage per row, first
 * measure), otherwise the measures of the first row (one stage per measure).
 */
export const funnelStages = (tile: LookerTile): Array<{ name: string; value: number }> => {
  const { dim, measures, data } = chartRows(tile);
  if (dim) {
    const m = measures[0];
    if (!m) return [];
    return data.flatMap((p) => (typeof p[m.name] === 'number' ? [{ name: p.x, value: p[m.name] as number }] : []));
  }
  const first = data[0];
  if (!first) return [];
  return measures.flatMap((m) => (typeof first[m.name] === 'number' ? [{ name: m.label, value: first[m.name] as number }] : []));
};

/**
 * value as a share of base, e.g. "1.35%". Ad funnels fall by orders of
 * magnitude per stage, so precision grows as the share shrinks: a real stage
 * never prints "0%". null when there is no meaningful base.
 */
export const formatSharePercent = (value: number, base: number): string | null => {
  if (!Number.isFinite(value) || !Number.isFinite(base) || base <= 0) return null;
  const pct = (value / base) * 100;
  const digits: Intl.NumberFormatOptions =
    pct === 0 || Math.abs(pct) >= 10 ? { maximumFractionDigits: 1 }
      : Math.abs(pct) >= 1 ? { maximumFractionDigits: 2 }
        : { maximumSignificantDigits: 2 };
  return `${pct.toLocaleString('en-US', digits)}%`;
};

/** Looker's "single record": the first row as label → text pairs. */
export const recordEntries = (tile: LookerTile): Array<{ label: string; text: string }> => {
  const row = tile.rows[0];
  if (!row) return [];
  return tile.fields.map((f) => ({ label: f.label, text: cellText(row[f.name]) ?? '—' }));
};

/** Inline style for a table cell from the author's conditional formatting; null = unstyled. */
export interface CellFormatStyle {
  backgroundColor?: string;
  color?: string;
  fontWeight?: number;
  fontStyle?: 'italic';
  textDecoration?: 'line-through';
}

const ruleMatches = (r: LookerConditionalFormat, v: number | null): boolean => {
  const [a, b] = r.values;
  if (r.test === 'null') return v === null;
  if (r.test === 'not null') return v !== null;
  // "No data" is not a number: it never equals, exceeds or falls between anything.
  if (v === null) return false;
  switch (r.test) {
    case 'equal to': return v === a;
    case 'not equal to': return v !== a;
    case 'greater than': return v > a;
    case 'less than': return v < a;
    case 'between': return v >= Math.min(a, b) && v <= Math.max(a, b);
    case 'not between': return v < Math.min(a, b) || v > Math.max(a, b);
    default: return false;
  }
};

export const cellFormatStyle = (
  rules: LookerConditionalFormat[],
  field: LookerField,
  cell: LookerCell | undefined,
): CellFormatStyle | null => {
  if (field.kind !== 'measure') return null;
  const v = toChartNumber(cell?.value ?? null);
  const r = rules.find((rule) => (rule.fields === null || rule.fields.includes(field.name)) && ruleMatches(rule, v));
  if (!r) return null;
  const style: CellFormatStyle = {
    ...(r.background ? { backgroundColor: r.background } : {}),
    ...(r.font ? { color: r.font } : {}),
    ...(r.bold ? { fontWeight: 700 } : {}),
    ...(r.italic ? { fontStyle: 'italic' as const } : {}),
    ...(r.strike ? { textDecoration: 'line-through' as const } : {}),
  };
  return Object.keys(style).length > 0 ? style : null;
};

/** Where a reference line sits: a number as given, or the statistic over the series' values. */
export const referenceValue = (v: LookerReferenceValue, values: number[]): number | null => {
  if (typeof v === 'number') return v;
  if (values.length === 0) return null;
  if (v === 'max') return Math.max(...values);
  if (v === 'min') return Math.min(...values);
  if (v === 'mean') return values.reduce((a, b) => a + b, 0) / values.length;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Looker labels may say "Average: {{mean}}"; the placeholders become numbers. */
export const fillReferenceLabel = (label: string | null, values: number[]): string | null =>
  label === null
    ? null
    : label.replace(/\{\{\s*(mean|median|max|min)\s*\}\}/g, (_m, stat: 'mean' | 'median' | 'max' | 'min') => {
      const n = referenceValue(stat, values);
      return n === null ? '—' : compactNumber(n);
    });

export interface SingleValueComparison {
  type: LookerComparison['type'];
  /** The comparison field's text, as Looker formatted it. */
  text: string;
  /** null = the author hid the label. */
  label: string | null;
  /** `change` only. */
  direction: 'up' | 'down' | 'flat' | null;
  /** `change` only: is this direction good news; null when flat. */
  good: boolean | null;
  /** `progress*` only: main ÷ comparison for the bar, 0–1. null when the comparison is not positive. */
  progress: number | null;
  percentText: string | null;
}

/**
 * Looker compares a single value with the next field after it. A comparison
 * with no value is not shown — never drawn as a zero.
 */
export const comparisonOf = (tile: LookerTile): SingleValueComparison | null => {
  const c = tile.vis.comparison;
  if (!c) return null;
  const mainIndex = Math.max(0, tile.fields.findIndex((f) => f.kind === 'measure'));
  const main = tile.fields[mainIndex];
  const comp = tile.fields[mainIndex + 1];
  const row = tile.rows[0];
  if (!main || !comp || !row) return null;
  const compValue = toChartNumber(row[comp.name]?.value ?? null);
  const text = cellText(row[comp.name]);
  if (compValue === null || text === null) return null;
  const mainValue = toChartNumber(row[main.name]?.value ?? null);

  const direction = c.type === 'change' ? (compValue > 0 ? 'up' : compValue < 0 ? 'down' : 'flat') : null;
  const isProgress = c.type === 'progress' || c.type === 'progress_percentage';
  const ratio = isProgress && mainValue !== null && compValue > 0 ? mainValue / compValue : null;
  return {
    type: c.type,
    text,
    label: c.showLabel ? c.label ?? comp.label : null,
    direction,
    good: direction === 'up' ? !c.reverseColors : direction === 'down' ? c.reverseColors : null,
    progress: ratio === null ? null : Math.min(1, Math.max(0, ratio)),
    percentText: ratio === null || mainValue === null ? null : formatSharePercent(mainValue, compValue),
  };
};
